import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';
import { FALLBACK_PARTNER_LOGOS } from '../../../api/modules/trustedPartners';
import { useTrustedPartnerLogos } from '../../../hooks/useTrustedPartnerLogos';
/**
 * TrustedPartnersReel Component
 * "TRUSTED BY TOP PARTNERS" title with purple nebula gradient bar
 * and infinitely scrolling partner logo train
 * Desktop: Absolute positioned layout within 1024px scaler
 * Mobile: Stubbed for future implementation
 *
 * Logos load from realtime-api marketing config; hard-coded Imgur URLs
 * are the fallback when the API fails or returns an empty list.
 *
 * Endless-marquee mechanics (the standard pattern used by company sites):
 *  - The track renders N identical copies of the logo set. N is computed so
 *    the track is always at least ~2x the viewport width, so the reel never
 *    shows empty space and always has content in flight.
 *  - A requestAnimationFrame loop advances ONE shared offset and translates
 *    the track by -Math.round(offset) pixels each frame (whole pixels => no
 *    sub-pixel jitter). When the offset passes one copy's width it wraps back
 *    by exactly that width, so the loop is seamless and endless.
 *  - Speed is derived from the measured copy width (one copy scrolls by in
 *    65s desktop / 30s mobile, matching the original CSS reel), so the pacing
 *    is constant regardless of how many or how wide the uploaded logos are.
 *  - Images are preloaded before the swap, so widths settle once on load and
 *    never change again - the loop is fully deterministic after that.
 */
const PURPLE_NEBULA_GRADIENT = [
  'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 10%, rgba(0,0,0,0) 90%, rgba(0,0,0,1) 100%)',
  'linear-gradient(90deg, rgba(80,60,255,0.55) 0%, rgba(120,70,255,0.55) 75%, rgba(180,60,255,0.55) 100%)',
  'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
  'radial-gradient(1000px at 15% 35%, rgba(100,80,255,0.40), rgba(40,0,70,0) 75%)',
  'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
  'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.30), rgba(45,0,70,0) 80%)',
  'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
  'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
].join(', ');
const PURPLE_NEBULA_BLEND =
  'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply';
export const TrustedPartnersReel = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const mobileTrackRef = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const { logos: remoteLogos, loading } = useTrustedPartnerLogos();
  const [partnerLogos, setPartnerLogos] = useState<string[]>(FALLBACK_PARTNER_LOGOS);
  const [copyCount, setCopyCount] = useState(2);
  // Preload + swap: only swap when every remote logo has settled (and at
  // least one decoded), so the DOM never shows broken/blank images.
  useEffect(() => {
    if (
      loading ||
      remoteLogos.length === 0 ||
      remoteLogos === partnerLogos
    ) {
      return;
    }
    let cancelled = false;
    let pending = remoteLogos.length;
    let loaded = 0;
    const done = (success: boolean) => {
      if (cancelled) return;
      if (success) loaded++;
      pending--;
      if (pending <= 0 && loaded > 0) setPartnerLogos(remoteLogos);
    };
    remoteLogos.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.decode().catch(() => {});
      if (img.complete) {
        done(img.naturalWidth > 0);
      } else {
        img.addEventListener('load', () => done(true), { once: true });
        img.addEventListener('error', () => done(false), { once: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loading, remoteLogos, partnerLogos]);
    useEffect(() => {
      const updateCount = () => {
        const desktop = trackRef.current;
        let track = desktop;
        let first = desktop?.firstElementChild as HTMLElement | null;
        if (!first || first.getBoundingClientRect().width <= 0) {
          track = mobileTrackRef.current;
          first = track?.firstElementChild as HTMLElement | null;
        }
        const container = track?.parentElement;
        if (!first || !container) return;
        const copyWidth = first.getBoundingClientRect().width;
        const viewWidth = container.getBoundingClientRect().width;
        if (copyWidth <= 0 || viewWidth <= 0) return;
        const needed = Math.max(2, Math.ceil((viewWidth / copyWidth) * 2) + 2);
        setCopyCount((prev) => (prev === needed ? prev : needed));
      };
      updateCount();
      // Retry until images load and a real width is measurable.
      const id = window.setInterval(updateCount, 500);
      window.addEventListener('resize', updateCount);
      return () => {
        window.clearInterval(id);
        window.removeEventListener('resize', updateCount);
      };
    }, [partnerLogos]);
  // Endless-loop sizing: render enough copies that the track is ~2x the
  // visible viewport (so the reel is always full and loops without gaps).
  // rAF marquee: one shared offset, whole-pixel transforms, seamless wrap.
  useEffect(() => {
    let offset = 0;
    let width = 0;
    let duration = 65000;
    let raf = 0;
    let last = performance.now();
    const measure = () => {
      const desktop = trackRef.current;
      let first = desktop?.firstElementChild as HTMLElement | null;
      if (!first || first.getBoundingClientRect().width <= 0) {
        first = mobileTrackRef.current?.firstElementChild as HTMLElement | null;
      }
      width = first ? first.getBoundingClientRect().width : 0;
      if (width > 0) {
        duration = window.matchMedia('(min-width: 1024px)').matches ? 65000 : 30000;
      }
    };
    const apply = () => {
      const px = -Math.round(offset);
      if (trackRef.current) trackRef.current.style.transform = `translateX(${px}px)`;
      if (mobileTrackRef.current) mobileTrackRef.current.style.transform = `translateX(${px}px)`;
    };
    const step = (now: number) => {
      const dt = now - last;
      last = now;
      measure();
      if (width > 0) {
        // One copy scrolls by in duration ms => constant perceived speed.
        offset += (width / duration) * dt;
        if (offset >= width) offset -= width;
        apply();
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [partnerLogos, copyCount]);
  // Spacing model: every image carries `marginRight: 12px` (including the
  // last in each copy) and the cycle has no extra padding/gap. That makes
  // the copy width exactly `sum(img + 12px)`, so the wrap offset (one copy)
  // lands on an identical copy start with zero overlap.
  const renderCycle = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      {partnerLogos.map((src, j) => (
        <img
          key={j}
          src={src}
          alt=""
          style={{
            height: '36px',
            width: 'auto',
            flexShrink: 0,
            display: 'block',
            marginRight: '12px',
          }}
        />
      ))}
    </div>
  );
  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '155px',
          backgroundColor: '#000000',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Title */}
          <div
            style={{
              position: 'absolute',
              left: '221px',
              top: '0px',
              width: '586px',
              zIndex: 5,
            }}
          >
            <p
              style={{
                lineHeight: '50px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '40px',
                letterSpacing: '-0.5px',
                color: 'rgba(255, 255, 255, 1)',
                margin: 0,
                fontVariationSettings: "'wght' 900",
              }}
            >
              TRUSTED BY TOP PARTNERS
            </p>
          </div>
          {/* Purple Nebula Gradient Background */}
          <div
            style={{
              position: 'absolute',
              left: '-10px',
              top: '68px',
              width: '1044px',
              height: '74px',
              zIndex: 1,
              overflow: 'hidden',
              background: PURPLE_NEBULA_GRADIENT,
              backgroundBlendMode: PURPLE_NEBULA_BLEND,
            }}
          />
          {/* Logo Train - vertically centered on the gradient strip */}
          <div
            style={{
              position: 'absolute',
              left: '-9px',
              top: '68px',
              width: '1032px',
              height: '74px',
              zIndex: 5,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              ref={trackRef}
              className="reel-track"
              style={{
                display: 'flex',
                alignItems: 'center',
                width: 'max-content',
                flexShrink: 0,
                willChange: 'transform',
              }}
            >
              {Array.from({ length: copyCount }).map((_, i) => (
                <div key={i}>{renderCycle()}</div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.section>
      {/* Mobile Layout - same structure, screen clips the reel */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: '#000000',
          overflow: 'hidden',
          padding: '20px 0',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Title */}
        <p
          style={{
            lineHeight: '50px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '40px',
            letterSpacing: '-0.5px',
            color: 'rgba(255, 255, 255, 1)',
            margin: 0,
            textAlign: 'center',
            fontVariationSettings: "'wght' 900",
          }}
        >
          TRUSTED BY TOP PARTNERS
        </p>
        {/* Gradient strip + Logo train */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '74px',
            marginTop: '18px',
            overflow: 'hidden',
          }}
        >
          {/* Purple Nebula Gradient Background */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: PURPLE_NEBULA_GRADIENT,
              backgroundBlendMode: PURPLE_NEBULA_BLEND,
            }}
          />
          {/* Logo Train */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <div
              ref={mobileTrackRef}
              className="reel-track"
              style={{
                display: 'flex',
                alignItems: 'center',
                width: 'max-content',
                flexShrink: 0,
                willChange: 'transform',
              }}
            >
              {Array.from({ length: copyCount }).map((_, i) => (
                <div key={i}>{renderCycle()}</div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};
